
const META_PIXEL_ID = (() => {
  const envValue =
    typeof process !== "undefined" && process && process.env
      ? process.env.REACT_APP_META_PIXEL_ID
      : undefined;
  if (envValue && envValue.trim()) {
    return envValue.trim();
  }
  return "";
})();
const META_PIXEL_SCRIPT_URL = "https://connect.facebook.net/en_US/fbevents.js";
const INIT_FLAG = "__META_PIXEL_INITIALIZED__";
const WARN_FLAG = "__META_PIXEL_WARNED__";
const DEBUG_FLAG = "__TYK_META_PIXEL_DEBUG__";
const DIAGNOSTIC_KEY = "__TYK_META_PIXEL_EVENTS__";

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";

const warnMissingPixelId = () => {
  if (!isBrowser()) {
    return;
  }
  if (window[WARN_FLAG]) {
    return;
  }
  logWarning("Meta Pixel ID is not configured. Set REACT_APP_META_PIXEL_ID to enable tracking.");
  window[WARN_FLAG] = true;
};

export const initMetaPixel = () => {
  if (!isBrowser()) {
    return false;
  }

  const pixelId = META_PIXEL_ID;
  if (!pixelId) {
    warnMissingPixelId();
    return false;
  }

  if (window[INIT_FLAG]) {
    return true;
  }

  if (!window.fbq) {
    (function (f, b, e, v, n, t, s) {
      if (f.fbq) {
        return;
      }
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) {
        f._fbq = n;
      }
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, "script", META_PIXEL_SCRIPT_URL);
  }

  window.fbq("init", pixelId);
  window[INIT_FLAG] = true;
  logInfo("Initialized Meta Pixel", { pixelId });
  return true;
};

const pushDiagnostic = (entry) => {
  if (!isBrowser()) {
    return;
  }

  const payload = {
    timestamp: new Date().toISOString(),
    ...entry,
  };

  if (!Array.isArray(window[DIAGNOSTIC_KEY])) {
    window[DIAGNOSTIC_KEY] = [];
  }

  window[DIAGNOSTIC_KEY].push(payload);

  if (typeof window.dispatchEvent === "function") {
    try {
      window.dispatchEvent(new CustomEvent("tyk:metaPixel", { detail: payload }));
    } catch (error) {
      if (typeof window.Event === "function") {
        const fallbackEvent = new Event("tyk:metaPixel");
        fallbackEvent.detail = payload;
        window.dispatchEvent(fallbackEvent);
      }
    }
  }
};

const shouldEmitConsoleLogs = () => {
  if (process.env.NODE_ENV !== "production") {
    return true;
  }

  if (!isBrowser()) {
    return false;
  }

  return Boolean(window[DEBUG_FLAG]);
};

const logInfo = (message, context) => {
  pushDiagnostic({ level: "info", message, context });

  if (!shouldEmitConsoleLogs()) {
    return;
  }

  // eslint-disable-next-line no-console
  console.info(`[MetaPixel] ${message}`, context);
};

const logWarning = (message, context) => {
  pushDiagnostic({ level: "warn", message, context });

  if (!shouldEmitConsoleLogs()) {
    return;
  }

  // eslint-disable-next-line no-console
  console.warn(`[MetaPixel] ${message}`, context);
};

const logError = (message, context) => {
  pushDiagnostic({ level: "error", message, context });

  if (!shouldEmitConsoleLogs()) {
    return;
  }

  // eslint-disable-next-line no-console
  console.error(`[MetaPixel] ${message}`, context);
};

export const trackMetaPixel = (eventName, params = {}, { standard = false } = {}) => {
  try {
    if (!initMetaPixel()) {
      logWarning("Initialization skipped—Meta Pixel ID missing or environment unavailable.", {
        eventName,
        params,
        standard,
      });
      return;
    }
  } catch (error) {
    logError("Initialization failed", { eventName, params, standard, error });
    return;
  }

  const method = standard ? "track" : "trackCustom";
  const fbq = typeof window !== "undefined" ? window.fbq : undefined;

  if (typeof fbq !== "function") {
    logWarning("fbq is unavailable—event not sent", { eventName, params, method });
    return;
  }

  try {
    fbq(method, eventName, params);
    logInfo(`Sent ${method} event`, { eventName, params });
  } catch (error) {
    logError("Failed to send event", { eventName, params, error });
  }
};

export const trackStandardMetaPixel = (eventName, params = {}) => {
  trackMetaPixel(eventName, params, { standard: true });
};

export const trackPageView = (params = {}) => {
  trackStandardMetaPixel("PageView", params);
};

export const getMetaPixelId = () => META_PIXEL_ID;

export const enableMetaPixelDebug = () => {
  if (!isBrowser()) {
    return false;
  }

  window[DEBUG_FLAG] = true;
  return true;
};

export const getMetaPixelDiagnostics = () => {
  if (!isBrowser()) {
    return [];
  }

  if (!Array.isArray(window[DIAGNOSTIC_KEY])) {
    return [];
  }

  return [...window[DIAGNOSTIC_KEY]];
};
